import { resolveAnchorPresence, subscribeTourViews } from "./tourViews";

export type AnchorWaitResult<T> = {
  found: T | null;
  reason: "found" | "manifest" | "timeout" | "aborted";
};

const CONFIRMATION_SAFETY_MS = 100;

const OBSERVE_OPTIONS: MutationObserverInit = {
  childList: true,
  subtree: true,
  attributes: true,
  attributeFilter: ["data-tour", "class", "style", "hidden"],
};

export const queryVisibleAnchor = (anchorId: string): HTMLElement | null => {
  const el = document.querySelector<HTMLElement>(`[data-tour="${CSS.escape(anchorId)}"]`);
  if (el && el.getClientRects().length > 0) {
    return el;
  }
  return null;
};

export const waitForAnchor = (
  anchorId: string,
  timeoutMs = 4000,
  signal?: AbortSignal,
): Promise<HTMLElement | null> => {
  return new Promise((resolve) => {
    const immediate = queryVisibleAnchor(anchorId);
    if (immediate) {
      resolve(immediate);
      return;
    }

    if (signal?.aborted) {
      resolve(null);
      return;
    }

    let settled = false;

    const finish = (result: HTMLElement | null): void => {
      if (settled) {
        return;
      }
      settled = true;
      clearTimeout(timer);
      observer.disconnect();
      signal?.removeEventListener("abort", onAbort);
      resolve(result);
    };

    const onAbort = (): void => {
      finish(null);
    };

    const observer = new MutationObserver(() => {
      const found = queryVisibleAnchor(anchorId);
      if (found) {
        finish(found);
      }
    });

    const timer = setTimeout(() => {
      finish(null);
    }, timeoutMs);

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["data-tour", "class", "style", "hidden"],
    });

    signal?.addEventListener("abort", onAbort);
  });
};

export const waitForFirstAnchor = (
  anchorIds: string[],
  timeoutMs: number,
  signal?: AbortSignal,
): Promise<{ anchorId: string; el: HTMLElement } | null> => {
  const findFirst = (): { anchorId: string; el: HTMLElement } | null => {
    for (const anchorId of anchorIds) {
      const el = queryVisibleAnchor(anchorId);
      if (el) {
        return { anchorId, el };
      }
    }
    return null;
  };

  return new Promise((resolve) => {
    const immediate = findFirst();
    if (immediate || signal?.aborted) {
      resolve(immediate);
      return;
    }

    let settled = false;

    const finish = (result: { anchorId: string; el: HTMLElement } | null): void => {
      if (settled) {
        return;
      }
      settled = true;
      clearTimeout(timer);
      observer.disconnect();
      signal?.removeEventListener("abort", onAbort);
      resolve(result);
    };

    const onAbort = (): void => {
      finish(null);
    };

    const observer = new MutationObserver(() => {
      const found = findFirst();
      if (found) {
        finish(found);
      }
    });

    const timer = setTimeout(() => {
      finish(null);
    }, timeoutMs);

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["data-tour", "class", "style", "hidden"],
    });

    signal?.addEventListener("abort", onAbort);
  });
};

const findFirstVisibleAnchor = (
  anchorIds: string[],
): { anchorId: string; el: HTMLElement } | null => {
  for (const anchorId of anchorIds) {
    const el = queryVisibleAnchor(anchorId);
    if (el) {
      return { anchorId, el };
    }
  }
  return null;
};

const waitSettled = <T>(
  anchorIds: string[],
  find: () => T | null,
  guardTimeoutMs: number,
  signal?: AbortSignal,
): Promise<AnchorWaitResult<T>> => {
  return new Promise((resolve) => {
    const immediate = find();
    if (immediate) {
      resolve({ found: immediate, reason: "found" });
      return;
    }

    if (signal?.aborted) {
      resolve({ found: null, reason: "aborted" });
      return;
    }

    let done = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let safetyTimer: ReturnType<typeof setTimeout> | null = null;
    let observer: MutationObserver | null = null;
    let unsubscribe: (() => void) | null = null;

    const finish = (result: AnchorWaitResult<T>): void => {
      if (done) {
        return;
      }
      done = true;
      if (timer !== null) {
        clearTimeout(timer);
      }
      if (safetyTimer !== null) {
        clearTimeout(safetyTimer);
      }
      observer?.disconnect();
      unsubscribe?.();
      signal?.removeEventListener("abort", onAbort);
      resolve(result);
    };

    const onAbort = (): void => {
      finish({ found: null, reason: "aborted" });
    };

    let confirmationPending = false;

    const scheduleConfirmation = (): void => {
      if (confirmationPending) {
        return;
      }
      confirmationPending = true;
      let confirmed = false;
      const confirm = (): void => {
        if (confirmed) {
          return;
        }
        confirmed = true;
        confirmationPending = false;
        if (safetyTimer !== null) {
          clearTimeout(safetyTimer);
          safetyTimer = null;
        }
        evaluate(true);
      };
      if (typeof requestAnimationFrame === "undefined") {
        safetyTimer = setTimeout(confirm, 0);
        return;
      }
      requestAnimationFrame(() => requestAnimationFrame(confirm));
      safetyTimer = setTimeout(confirm, CONFIRMATION_SAFETY_MS);
    };

    const evaluate = (confirmed = false): void => {
      if (done) {
        return;
      }
      const found = find();
      if (found) {
        finish({ found, reason: "found" });
        return;
      }
      const presences = anchorIds.map(resolveAnchorPresence);
      if (presences.every((presence) => presence === "absent")) {
        if (confirmed) {
          finish({ found: null, reason: "manifest" });
        } else {
          scheduleConfirmation();
        }
        return;
      }
      if (presences.includes("pending")) {
        if (timer !== null) {
          clearTimeout(timer);
          timer = null;
        }
        return;
      }
      if (timer === null) {
        timer = setTimeout(() => {
          finish({ found: null, reason: "timeout" });
        }, guardTimeoutMs);
      }
    };

    observer = new MutationObserver(() => evaluate());
    observer.observe(document.body, OBSERVE_OPTIONS);
    unsubscribe = subscribeTourViews(() => evaluate());
    signal?.addEventListener("abort", onAbort);
    evaluate();
  });
};

export const waitForAnchorSettled = (
  anchorId: string,
  guardTimeoutMs: number,
  signal?: AbortSignal,
): Promise<AnchorWaitResult<HTMLElement>> =>
  waitSettled([anchorId], () => queryVisibleAnchor(anchorId), guardTimeoutMs, signal);

export const waitForFirstAnchorSettled = (
  anchorIds: string[],
  guardTimeoutMs: number,
  signal?: AbortSignal,
): Promise<AnchorWaitResult<{ anchorId: string; el: HTMLElement }>> =>
  waitSettled(anchorIds, () => findFirstVisibleAnchor(anchorIds), guardTimeoutMs, signal);

export default waitForAnchor;
